import { Apple, Play, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HERO } from "@/constants/testIds";
import { BeninFlag } from "@/components/landing/BeninFlag";
import { ZafaraLogoMark } from "@/components/landing/Logo";
import { useContent } from "@/context/ContentContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const navigate = useNavigate();
  const { content } = useContent();
  const h = content.hero;
  const goDownload = (e) => {
    e.preventDefault();
    navigate("/telecharger");
  };
  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative overflow-hidden pt-14 pb-24 md:pt-20 md:pb-32"
    >
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#F5A800]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#0E7A3B]/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-utility uppercase tracking-widest text-[#0E7A3B]">
              <Sparkles className="h-3.5 w-3.5" />
              {h.badge}
            </div>

            {/* SEO H1 — semantic + keywords */}
            <h1 className="mt-6 sr-only">
              Zafara, la marketplace 100% béninoise pour acheter et vendre en ligne.
            </h1>

            {/* Visual display headline (accessible sibling of the semantic H1) */}
            <p
              aria-hidden="true"
              className="mt-6 font-display text-[#16241B] text-5xl sm:text-6xl lg:text-[5.25rem] leading-[0.95] tracking-tighter"
            >
              {h.line1}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{h.highlight}</span>
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 -bottom-1 h-3 bg-[#F5A800]/70 -z-0"
                />
              </span>
              <br />
              {h.line2}
              <br />
              <span className="text-[#0E7A3B]">{h.line3}</span>
            </p>

            <p className="mt-8 max-w-xl text-lg text-[#16241B]/75 leading-relaxed">
              {h.paragraph}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                data-testid={HERO.ctaAppStore}
                href="/telecharger"
                onClick={goDownload}
                className="group inline-flex items-center gap-3 rounded-2xl bg-[#16241B] px-5 py-3.5 min-w-[200px] text-white shadow-[0_10px_30px_rgba(22,36,27,0.25)] hover:-translate-y-0.5 hover:shadow-[0_14px_38px_rgba(22,36,27,0.35)] transition-all duration-300"
              >
                <Apple className="h-7 w-7" />
                <div className="text-left">
                  <p className="font-utility text-[10px] uppercase tracking-widest opacity-70">
                    Télécharger sur
                  </p>
                  <p className="font-display font-bold text-lg leading-tight">
                    App Store
                  </p>
                </div>
              </a>
              <a
                data-testid={HERO.ctaPlayStore}
                href="/telecharger"
                onClick={goDownload}
                className="group inline-flex items-center gap-3 rounded-2xl bg-[#0E7A3B] px-5 py-3.5 min-w-[200px] text-white shadow-[0_10px_30px_rgba(14,122,59,0.35)] hover:-translate-y-0.5 hover:shadow-[0_14px_38px_rgba(14,122,59,0.45)] transition-all duration-300"
              >
                <Play className="h-7 w-7 fill-white" />
                <div className="text-left">
                  <p className="font-utility text-[10px] uppercase tracking-widest opacity-80">
                    Disponible sur
                  </p>
                  <p className="font-display font-bold text-lg leading-tight">
                    Google Play
                  </p>
                </div>
              </a>
            </div>

            <div className="mt-8 flex items-center gap-3 text-xs font-utility uppercase tracking-widest text-[#16241B]/60">
              <BeninFlag className="h-3 w-[18px]" />
              Édité par <span className="text-[#16241B] font-semibold">Whilsco-Digit</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center">
            <div className="relative">
              <div className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-gradient-to-br from-[#F5A800]/20 via-transparent to-[#0E7A3B]/20 blur-2xl" />

              <div className="relative w-[280px] sm:w-[320px] aspect-[9/19] rounded-[3rem] bg-[#16241B] p-3 shadow-[0_40px_100px_rgba(22,36,27,0.35)] rotate-3 hover:rotate-0 transition-transform duration-700">
                <div className="relative h-full w-full rounded-[2.4rem] bg-gradient-to-br from-[#FBFAF6] via-white to-[#E7F3EC] overflow-hidden">
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 h-6 w-24 rounded-full bg-[#16241B] z-20" />

                  <div className="pt-14 px-5 pb-5 h-full flex flex-col">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col items-start">
                        <div className="flex items-baseline">
                          <span className="font-display font-bold text-2xl text-[#0E7A3B] leading-none">Za</span>
                          <span className="font-display font-bold text-2xl text-[#F5A800] leading-none">fara</span>
                        </div>
                        <span className="font-utility text-[9px] uppercase tracking-widest text-[#16241B]/60 mt-0.5">
                          Bénin
                        </span>
                      </div>
                      <div className="h-8 w-8 rounded-full bg-[#0E7A3B]/10 flex items-center justify-center">
                        <div className="h-4 w-4 rounded-full bg-[#0E7A3B]" />
                      </div>
                    </div>

                    <div className="mt-5 rounded-2xl bg-white border border-[#EDE7D9] p-3">
                      <p className="font-utility text-[9px] uppercase tracking-widest text-[#16241B]/50">
                        Bonjour Amaka
                      </p>
                      <p className="mt-1 font-display font-bold text-base text-[#16241B]">
                        Qu'est-ce qui vous ferait plaisir aujourd'hui ?
                      </p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                      {[
                        { label: "Livraison", c: "#0E7A3B" },
                        { label: "Marché", c: "#F5A800" },
                        { label: "Services", c: "#0E7A3B" },
                        { label: "Bons plans", c: "#F5A800" },
                      ].map((it, i) => (
                        <div key={i} className="rounded-xl bg-white border border-[#EDE7D9] p-2.5">
                          <div className="h-6 w-6 rounded-lg" style={{ background: it.c }} />
                          <p className="mt-2 font-display font-bold text-xs text-[#16241B]">{it.label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto rounded-2xl bg-[#16241B] text-white p-3">
                      <p className="font-utility text-[9px] uppercase tracking-widest opacity-70">
                        Votre commande
                      </p>
                      <p className="mt-1 font-display font-bold text-sm">
                        Arrive dans 22 min
                      </p>
                      <div className="mt-2 h-1.5 rounded-full bg-white/15 overflow-hidden">
                        <div className="h-full w-2/3 bg-[#F5A800]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-6 -left-10 hidden md:block rounded-2xl bg-white shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-[#EDE7D9] p-3">
                <ZafaraLogoMark className="h-16 w-16" />
              </div>

              <div className="absolute -bottom-6 -right-6 hidden md:block rounded-2xl bg-[#F5A800] text-[#16241B] p-4 shadow-[0_20px_50px_rgba(0,0,0,0.2)]">
                <p className="font-display font-bold text-2xl">4,8/5</p>
                <p className="font-utility text-[10px] uppercase tracking-widest opacity-80 mt-0.5">
                  Note bêta
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
